import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import FeatureFlag from "@/domain/enums/FeatureFlag";
import { useToast } from "@/hooks/utils/useToast";
import { emailAttachmentToDisplay } from "@/mappers/email/displayAttachmentMapper";
import { useFeatureFlag } from "@cs_teamwaydev/mailvista-sdk_react";
import { Paperclip } from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import AttachmentChip from "../widgets/attachements/AttachmentChip";
import getAttachmentIcon from "../widgets/attachements/attachmentIcon";
import DocumentPreview from "../widgets/attachements/DocumentPreview";

export enum AttachmentAction {
    ADD,
    REMOVE,
}

interface AttachmentButtonProps {
    onAttachmentsChange?: (
        attachments: EmailAttachment[],
        action: AttachmentAction,
    ) => void;
    attachments?: EmailAttachment[];
}

const AttachmentButton = ({
    onAttachmentsChange,
    attachments = [],
}: AttachmentButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedAttachment, setSelectedAttachment] =
        useState<EmailAttachment | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isDeviceStorageAccessEnabled = useFeatureFlag(
        FeatureFlag.DEVICESTORAGE_ENABLE_ACCESS,
    );
    const { t } = useTranslation();
    const { toast } = useToast();

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isDeviceStorageAccessEnabled) {
            toast({
                title: t("attachments.deviceStorageDisabled.title"),
                description: t("attachments.deviceStorageDisabled.description"),
                variant: "destructive",
            });

            return;
        }

        const { files } = event.target;
        if (!files || !onAttachmentsChange) return;

        const newAttachments = Array.from(files).map(
            (file) =>
                new Promise<EmailAttachment>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const readerResult = reader.result as string;
                        const removedDataUrlPrefix = readerResult.replace(
                            /^data:.*?;base64,/,
                            "",
                        );

                        resolve(
                            new EmailAttachment({
                                id: crypto.randomUUID(),
                                name: file.name,
                                size: file.size,
                                type: file.type,
                                path: removedDataUrlPrefix,
                                preview: readerResult,
                                createdAt: new Date(),
                                modifiedAt: new Date(),
                            }),
                        );
                    };
                    reader.readAsDataURL(file);
                }),
        );

        Promise.all(newAttachments).then((resolvedAttachments) => {
            onAttachmentsChange(
                [...attachments, ...resolvedAttachments],
                AttachmentAction.ADD,
            );
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        });
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handlePreviewOpen = (attachment: EmailAttachment, index: number) => {
        setSelectedAttachment(attachment);
        setSelectedIndex(index);
    };

    const handlePrevious = () => {
        if (attachments && selectedIndex > 0) {
            setSelectedIndex(selectedIndex - 1);
            setSelectedAttachment(attachments[selectedIndex - 1]);
        }
    };

    const handleNext = () => {
        if (attachments && selectedIndex < attachments.length - 1) {
            setSelectedIndex(selectedIndex + 1);
            setSelectedAttachment(attachments[selectedIndex + 1]);
        }
    };

    return (
        <>
            <input
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
                type="file"
                multiple
            />
            <div className="flex items-center gap-2">
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span>
                                <Button
                                    disabled={!isDeviceStorageAccessEnabled}
                                    onClick={handleUploadClick}
                                    size="smallCircle"
                                    variant="ghost"
                                >
                                    <Paperclip className="h-4 w-4" />
                                </Button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {isDeviceStorageAccessEnabled
                                ? t("attachments.uploadFile")
                                : t("attachments.deviceStorageDisabled.title")}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                {attachments.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                        {attachments.map((attachment, index) => (
                            <AttachmentChip
                                key={attachment.id}
                                attachment={attachment}
                                onClick={() =>
                                    handlePreviewOpen(attachment, index)
                                }
                                onRemove={() =>
                                    onAttachmentsChange?.(
                                        attachments.filter(
                                            (a) => a.id !== attachment.id,
                                        ),
                                        AttachmentAction.REMOVE,
                                    )
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
            {selectedAttachment && (
                <DocumentPreview
                    attachment={emailAttachmentToDisplay(selectedAttachment)}
                    hasNext={selectedIndex < attachments.length - 1}
                    hasPrevious={selectedIndex > 0}
                    icon={getAttachmentIcon(selectedAttachment)}
                    isOpen={!!selectedAttachment}
                    onClose={() => setSelectedAttachment(null)}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                />
            )}
        </>
    );
};

export default AttachmentButton;
